import {
  getEditorGraphScopeId,
} from '../editor/editorSession.js';
import { createGraphRuntime, type GraphRuntime, type GraphRuntimeRenderSettings, type GraphRuntimeResolver } from '../graph/graphRuntime.js';
import { computeLayout, type LayoutResult, type LayoutSession, type SceneLayoutContext } from '../scene/layoutEngine.js';
import { createDefaultGeometrySlot, createDefaultMaterialSlot, type ResolvedGraph } from '../scene/layerState.js';
import { type Scene } from '../scene/sceneState.js';
import { toRenderSlot, type AppState } from '../state/appState.js';
import { resolveGeometrySlotForRender, resolveMaterialSlotForRender } from '../state/layerGraphSelectors.js';
import type { ViewportResolution } from './viewportResolution.js';

export interface StandaloneGraphs {
  geometry: ResolvedGraph;
  material: ResolvedGraph;
  graphScopeId?: string;
}

export interface BuildSceneContextOptions {
  standaloneGraphs?: StandaloneGraphs;
  renderTheme?: GraphRuntimeRenderSettings['renderTheme'];
  runtimeParams?: import('../graph/nodeDefs.js').ParamStore;
  behaviorParams?: Record<string, import('../graph/nodeDefs.js').ParamStore>;
  /** The running document's session: what its behaviours scrolled, folded, turned, showed and hid. */
  session?: LayoutSession;
}

export function getStandaloneGraphsFor(appState: AppState): StandaloneGraphs | null {
  if (appState.editorContext.mode === 'geometry-library') {
    const material = resolveMaterialSlotForRender(appState, createDefaultMaterialSlot());
    if (!material) return null;
    return {
      geometry: { nodes: appState.nodes, edges: appState.edges },
      material,
    };
  }

  if (appState.editorContext.mode === 'material-library') {
    const geometry = resolveGeometrySlotForRender(appState, createDefaultGeometrySlot());
    if (!geometry) return null;
    return {
      geometry,
      material: { nodes: appState.nodes, edges: appState.edges },
    };
  }

  if (appState.editorContext.mode === 'layer' && appState.editorContext.layerId) {
    const layerDef = appState.layerLibrary[appState.editorContext.layerId];
    if (!layerDef) return null;
    const activeRenderSlot = toRenderSlot(appState.editorGraphSlot);
    const geometry = activeRenderSlot === 'geometry'
      ? { nodes: appState.nodes, edges: appState.edges }
      : resolveGeometrySlotForRender(appState, layerDef.geometry);
    const material = activeRenderSlot === 'material'
      ? { nodes: appState.nodes, edges: appState.edges }
      : resolveMaterialSlotForRender(appState, layerDef.material);
    if (!geometry || !material) return null;
    return { geometry, material };
  }

  const geometry = resolveGeometrySlotForRender(appState, createDefaultGeometrySlot());
  const material = resolveMaterialSlotForRender(appState, createDefaultMaterialSlot());
  if (!geometry || !material) return null;
  return { geometry, material };
}

function getRuntimeRenderSettingsFor(
  appState: AppState,
  renderTheme: GraphRuntimeRenderSettings['renderTheme'],
): GraphRuntimeRenderSettings {
  return {
    colormap: appState.colormap,
    aaMode: appState.aaMode,
    aaWidth: appState.aaWidth,
    arcCoverageMode: appState.arcCoverageMode,
    renderTheme,
  };
}

export interface BuiltSceneContext {
  layoutResult: LayoutResult;
  layoutContext: SceneLayoutContext & { resolveRuntime: GraphRuntimeResolver };
}

export function buildSceneContextFor(
  appState: AppState,
  size: ViewportResolution | number,
  scene: Scene = appState.scene,
  options: BuildSceneContextOptions = {},
): BuiltSceneContext {
  const viewportSize = typeof size === 'number' ? { w: size, h: size } : size;
  const runtimeCache = new Map<string, GraphRuntime>();
  const renderSettings = getRuntimeRenderSettingsFor(appState, options.renderTheme);

  const resolveRuntime: GraphRuntimeResolver = (layerId) => {
    const cached = runtimeCache.get(layerId);
    if (cached) return cached;

    const layerDef = appState.layerLibrary[layerId];
    if (layerDef) {
      const resolvedGeometry = resolveGeometrySlotForRender(appState, layerDef.geometry);
      const resolvedMaterial = resolveMaterialSlotForRender(appState, layerDef.material);
      if (!resolvedGeometry) return null;
      const rt = createGraphRuntime(
        resolvedGeometry.nodes,
        resolvedGeometry.edges,
        resolvedMaterial,
        {
          graphId: layerId,
          renderSettings,
          sizeMode: resolvedGeometry.sizeMode,
          shapes: { ...appState.builtInShapes, ...appState.shapes },
          lights: appState.lights,
        },
      );
      runtimeCache.set(layerId, rt);
      return rt;
    }

    if (layerId === 'layer_main') {
      const preview = options.standaloneGraphs ?? getStandaloneGraphsFor(appState);
      if (!preview) return null;
      const rt = createGraphRuntime(
        preview.geometry.nodes,
        preview.geometry.edges,
        preview.material,
        {
          graphId: preview.graphScopeId ?? getEditorGraphScopeId(appState.editorContext) ?? 'layer_main',
          renderSettings,
          sizeMode: { kind: 'fill' },
          shapes: { ...appState.builtInShapes, ...appState.shapes },
          lights: appState.lights,
        },
      );
      runtimeCache.set(layerId, rt);
      return rt;
    }

    return null;
  };

  const rootSpace = { min: { w: 0, h: 0 }, max: { w: viewportSize.w, h: viewportSize.h } };
  const layoutContext: SceneLayoutContext & { resolveRuntime: GraphRuntimeResolver } = {
    componentLibrary: appState.componentLibrary,
    params: { ...appState.params, ...options.runtimeParams },
    behaviorParams: options.behaviorParams,
    session: options.session,
    resolveRuntime,
  };
  const layoutResult = computeLayout(scene, rootSpace, layoutContext);
  return { layoutResult, layoutContext };
}
